import * as React from "react";
import get from "lodash/get";
import { StoreState } from "../../reducers";
import { Animation, AnimationName } from "../../animations";
import { HorizontalAlignmentProp } from "../../utils/getHorizontalAlignment";
import { widgetEventTrack } from "../../actions";

export interface ComponentConfig {
  component: string;
  children?: string[];
  childrenStyle?: React.CSSProperties;
  style?: React.CSSProperties;
  src?: string;
  mode?: string;
  animationEnter?: AnimationName;
  animationLeave?: AnimationName;
  animationPosition?: string;
  checkedValue?: string;
  uncheckedValue?: string;
  validators?: string[][];
  horizontalAlign: HorizontalAlignmentProp;
  inputPlaceholderColor?: string;
  checkboxBorderColor?: string;
}

export interface WidgetConfig {
  SDKVersion: string;
  // tslint:disable-next-line:no-any
  data: { [index: string]: any };
  settings: { [index: string]: any };
  index: ComponentConfig;
  [key: string]: ComponentConfig | string | {};
}

export interface WidgetProps {
  key: string;
  children?: string[];
  config: WidgetConfig;
  data: {};
  configName: string;
  classNames?: string;
  style?: React.CSSProperties;
  onClickAnimation?: string;
  component: string;
  horizontalAlign: HorizontalAlignmentProp;
  // tslint:disable-next-line:no-any
  components: any;
  editingConfigName: string;
  isEditing: boolean;
  appleWallet: StoreState["referral"]["appleWallet"];
  googleWallet: StoreState["referral"]["googleWallet"];
  rewardStats: StoreState["reward"]["rewardStats"];
  referral: StoreState["referral"]["referral"];
  share: StoreState["share"]["share"];
  widgetEventTrack: typeof widgetEventTrack;
  validators?: string[][];
}

interface ElementBounds {
  left: number;
  right: number;
  top: number;
  bottom: number;
  width: number;
  height: number;
}

export class Widget extends React.PureComponent<WidgetProps> {
  componentDidUpdate(prevProps: WidgetProps) {
    const { config, configName, editingConfigName } = this.props;

    const shouldUpdateBounds =
      configName === editingConfigName && config.areGuideLinesVisible;
    const shouldRemoveBounds =
      (prevProps.editingConfigName === "Screen" &&
        editingConfigName === "Screen") ||
      (prevProps.config.areGuideLinesVisible && !config.areGuideLinesVisible);

    if (shouldUpdateBounds) {
      this.getBounds();
    }

    if (shouldRemoveBounds) {
      this.removeBoundLine("left");
      this.removeBoundLine("right");
      this.removeBoundLine("top");
      this.removeBoundLine("bottom");
    }
  }

  getBounds = () => {
    const { configName } = this.props;
    const element = document.getElementById(configName);
    const root = document.getElementById("Widget-Screen");
    const rootRect = root && root.getBoundingClientRect();
    const elementRect = element && element.getBoundingClientRect();

    if (elementRect && element && rootRect) {
      const elementBounds = {
        left: element.offsetLeft,
        right: rootRect.width - element.offsetLeft - elementRect.width,
        top: element.offsetTop,
        bottom: rootRect.height - element.offsetTop - elementRect.height,
        width: elementRect.width,
        height: elementRect.height,
      };

      this.createBoundLine("left", elementBounds);
      this.createBoundLine("right", elementBounds);
      this.createBoundLine("top", elementBounds);
      this.createBoundLine("bottom", elementBounds);
    }
  };

  createBoundLine = (bound: string, bounds: ElementBounds) => {
    this.removeBoundLine(bound);
    const root = document.getElementById("Widget-Screen");
    const boundLine = document.createElement("div");
    boundLine.id = `element-bounds-${bound}`;
    boundLine.classList.add("bound-line");
    boundLine.textContent = bounds[bound] + "px";

    switch (bound) {
      case "left": {
        boundLine.style.borderBottom = "1px solid red";
        boundLine.style.width = bounds[bound] + "px";
        boundLine.style.height = "10px";
        boundLine.style.left = "0";
        boundLine.style.top = bounds.top + bounds.height / 2 - 10 + "px";

        break;
      }

      case "right": {
        boundLine.style.borderBottom = "1px solid red";
        boundLine.style.width = bounds[bound] + "px";
        boundLine.style.height = "10px";
        boundLine.style.right = "0";
        boundLine.style.top = bounds.top + bounds.height / 2 - 10 + "px";
        break;
      }

      case "top": {
        boundLine.style.borderRight = "1px solid red";
        boundLine.style.width = "40px";
        boundLine.style.height = bounds[bound] + "px";
        boundLine.style.left = bounds.left + bounds.width / 2 - 40 + "px";
        boundLine.style.top = "0";
        break;
      }

      case "bottom": {
        boundLine.style.borderRight = "1px solid red";
        boundLine.style.width = "40px";
        boundLine.style.height = bounds[bound] + "px";
        boundLine.style.left = bounds.left + bounds.width / 2 - 40 + "px";
        boundLine.style.bottom = "0";
        break;
      }

      default:
        break;
    }

    if (root) {
      root.appendChild(boundLine);
    }
  };

  removeBoundLine = (bound: string) => {
    const root = document.getElementById("Widget-Screen");
    const currentBoundLine = document.getElementById(`element-bounds-${bound}`);

    if (currentBoundLine && root) {
      root.removeChild(currentBoundLine);
    }
  };

  createChildren() {
    const {
      appleWallet,
      config,
      data,
      editingConfigName,
      components,
      googleWallet,
      referral,
      rewardStats,
      share,
      widgetEventTrack,
    } = this.props;

    if (!this.props.children) {
      return;
    }

    const children = this.props.children as string[];

    return children.map((id) => {
      const childConfig = config[id] as ComponentConfig;

      return (
        <Widget
          key={id}
          configName={id}
          {...childConfig}
          appleWallet={appleWallet}
          data={data}
          config={config}
          components={components}
          editingConfigName={editingConfigName}
          googleWallet={googleWallet}
          rewardStats={rewardStats}
          referral={referral}
          share={share}
          isEditing={Boolean(get(config, "__editMode__", false))}
          widgetEventTrack={widgetEventTrack}
        />
      );
    });
  }

  render(): JSX.Element | null {
    const { component, components, ...props } = this.props;
    if (!component) {
      return null;
    }

    const Component = components[component];

    if (!Component) {
      console.warn(
        `Your config references the component ${component} but this component does not exist ` +
          `in the widget SDK. You might be using an old version of the widget-SDK.`
      );

      return null;
    }

    const componentConfig = (props.config[props.configName] ||
      {}) as ComponentConfig;
    const { animationEnter, animationLeave, animationPosition } =
      componentConfig;

    if (animationEnter || animationLeave) {
      return (
        <Animation
          animationEnter={animationEnter}
          animationLeave={animationLeave}
          position={animationPosition}
        >
          <Component {...props} children={this.createChildren()} />
        </Animation>
      );
    } else {
      return <Component {...props} children={this.createChildren()} />;
    }
  }
}
